import React, { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import {
  LBTabs,
  LBTabsList,
  LBTabsTrigger,
  LBDropdownMenu,
  LBDropdownMenuContent,
  LBDropdownMenuItem,
  LBDropdownMenuLabel,
  LBDropdownMenuSeparator,
  LBDropdownMenuTrigger,
  LBSwitch,
  LBAiInsightsIcon,
  LBButton,
  LBAvatar,
  LBAvatarFallback,
  LBAvatarImage,
  LBBadge,
  LBPopover,
  LBPopoverContent,
  LBPopoverTrigger,
  LBTable,
  LBTableBody,
  LBTableCell,
  LBTableHead,
  LBTableHeader,
  LBTableRow,
  LBSheet,
  LBSheetContent,
  LBSheetHeader,
  LBSheetTitle,
  LBScrollArea,
  LBSeparator
} from '@linearb/buzz'
import { Info, Menu, Sun, Moon, Monitor } from 'lucide-react'
import linearbLogo from '../images/linearb_logo_w_text.svg'
import useUserInfo from '../hooks/useUserInfo'
import { jwtStorage } from '../utils/jwtStorage'
import { getAllScopeDefinitions } from '../constants/scopes'
import {
  CURRENT_ENVIRONMENT,
  HAS_US_DATA_SOURCES,
  IS_PROD_EU
} from '../constants/apis'
import BackofficeScopeBadges from '../custom-components/BackofficeScopeBadges'
import EnvironmentSelector from './EnvironmentSelector'
import { ScopePreviewBadge, ScopePreviewMenu } from './ScopePreviewMenu'
import useTheme from '../hooks/useTheme'
import useInsightsPreferences from '../hooks/useInsightsPreferences'
import useTimeFormat from '../hooks/useTimeFormat'
import { trackAction } from '../lib/datadogRum'

export interface Route {
  key: string
  name: string
  route: string
  icon?: React.ReactNode
  visible?: boolean
}

interface TabNavigationProps {
  routes: Route[]
}

// Org-list columns that can be toggled on/off. AI Chats and Dashboards also gate
// their org-details sections; Health only controls the column.
const COLUMN_TOGGLES: {
  key: 'showAiChats' | 'showDashboards' | 'showHealth'
  label: string
  id: string
}[] = [
  { key: 'showAiChats', label: 'AI Chats', id: 'column-ai-chats-switch' },
  {
    key: 'showDashboards',
    label: 'Dashboards',
    id: 'column-dashboards-switch'
  },
  { key: 'showHealth', label: 'Health', id: 'column-health-switch' }
]

const TabNavigation: React.FC<TabNavigationProps> = ({ routes }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const userInfo = useUserInfo()
  const { theme, setTheme } = useTheme()
  const { prefs: insightsPrefs, setPrefs: setInsightsPrefs } =
    useInsightsPreferences()
  const { use24Hour, setUse24Hour } = useTimeFormat()

  // Filter only visible routes
  const visibleRoutes = routes.filter(({ visible = true }) => visible)
  // Menu-only pages are unregistered on some envs (hiddenOnEu)
  const hasRoute = (key: string) => routes.some(r => r.key === key)

  // Split into primary tabs and "More" dropdown items
  const MORE_TAB_KEYS = new Set([
    'projects',
    'workerb',
    'gitstream',
    'feature-requests',
    'git-org-search',
    'user-org-search',
    'slack-commands',
    'usage'
  ])
  const primaryRoutes = visibleRoutes.filter(r => !MORE_TAB_KEYS.has(r.key))
  const moreRoutes = visibleRoutes.filter(r => MORE_TAB_KEYS.has(r.key))

  // Determine active tab based on current path
  const getActiveTab = (): string => {
    // Check all routes (including hidden ones) for active state
    const currentRoute = routes.find(route =>
      location.pathname.startsWith(route.route)
    )
    // Only return the key if the route is visible, otherwise return empty
    if (currentRoute && visibleRoutes.find(r => r.key === currentRoute.key)) {
      return currentRoute.key
    }
    return ''
  }

  const activeTab = getActiveTab()
  const isMoreActive = MORE_TAB_KEYS.has(activeTab)
  const activeMoreRoute = isMoreActive
    ? moreRoutes.find(r => r.key === activeTab)
    : null
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleTabChange = (value: string): void => {
    const route = visibleRoutes.find(r => r.key === value)
    if (route) {
      navigate(route.route)
    }
  }

  const handleLogout = (): void => {
    jwtStorage.remove()
    navigate('/login')
  }

  // Get initials for avatar fallback
  const getInitials = (name: string): string => {
    if (!name) {
      return 'U'
    }
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <header className='fixed top-0 left-0 right-0 z-50 bg-background border-b border-border shadow-sm'>
      {/* EU marker so nobody mistakes prod-eu data for US */}
      {IS_PROD_EU && (
        <div className='absolute inset-x-0 top-0 h-1 bg-blue-600' />
      )}
      <div className='flex items-center justify-between px-3 md:px-6 py-3'>
        <div className='flex items-center gap-4 md:gap-8'>
          {/* Mobile hamburger button */}
          <LBButton
            variant='ghost'
            size='icon'
            className='md:hidden h-9 w-9'
            onClick={() => setMobileMenuOpen(true)}
            aria-label='Open navigation menu'
          >
            <Menu className='h-5 w-5' />
          </LBButton>
          <Link to='/' className='flex items-center gap-2 no-underline'>
            <img
              src={linearbLogo}
              alt='LinearB'
              className='h-8 dark:brightness-0 dark:invert'
            />
            <span className='text-lg font-semibold text-muted-foreground hidden sm:inline'>
              Backoffice
            </span>
          </Link>
          <LBTabs value={activeTab} onValueChange={handleTabChange}>
            <LBTabsList className='hidden md:flex'>
              {primaryRoutes.map(route => (
                <LBTabsTrigger
                  key={route.key}
                  value={route.key}
                  className='flex items-center gap-2'
                >
                  {route.icon}
                  {route.name}
                </LBTabsTrigger>
              ))}

              {/* More dropdown styled as a tab trigger */}
              {moreRoutes.length > 0 && (
                <LBDropdownMenu>
                  <LBDropdownMenuTrigger asChild>
                    <button
                      className={`inline-flex items-center justify-center whitespace-nowrap rounded-full px-5 py-1 text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 gap-2 ${
                        isMoreActive
                          ? 'bg-slate-200 text-slate-700'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {isMoreActive && activeMoreRoute?.icon}
                      {isMoreActive ? `${activeMoreRoute?.name} ▾` : 'More ▾'}
                    </button>
                  </LBDropdownMenuTrigger>
                  <LBDropdownMenuContent align='start'>
                    {moreRoutes.map(route => (
                      <LBDropdownMenuItem
                        key={route.key}
                        onClick={() => navigate(route.route)}
                        className={`cursor-pointer flex items-center gap-2 ${activeTab === route.key ? 'bg-accent font-medium' : ''}`}
                      >
                        {route.icon}
                        {route.name}
                      </LBDropdownMenuItem>
                    ))}
                  </LBDropdownMenuContent>
                </LBDropdownMenu>
              )}
            </LBTabsList>
          </LBTabs>
        </div>

        <div className='flex items-center gap-4'>
          <div className='hidden md:flex items-center gap-4'>
            <ScopePreviewBadge />

            {/* Environment Selector */}
            <EnvironmentSelector />
          </div>

          {/* Insights Preferences */}
          <LBDropdownMenu>
            <LBDropdownMenuTrigger asChild>
              <LBButton
                variant='ghost'
                size='icon'
                className='h-9 w-9'
                aria-label='Insights preferences'
              >
                <LBAiInsightsIcon className='h-4 w-4' />
              </LBButton>
            </LBDropdownMenuTrigger>
            <LBDropdownMenuContent align='end' className='w-52'>
              <LBDropdownMenuLabel>Insights</LBDropdownMenuLabel>
              <LBDropdownMenuSeparator />
              <div className='flex flex-col gap-3 px-2 py-3'>
                <div className='flex items-center justify-between gap-2'>
                  <label
                    htmlFor='insights-enabled-switch'
                    className='text-sm cursor-pointer select-none'
                  >
                    Enable insights
                  </label>
                  <LBSwitch
                    id='insights-enabled-switch'
                    checked={insightsPrefs.enabled}
                    onCheckedChange={checked => {
                      trackAction('insights_enabled_toggled', {
                        enabled: checked
                      })
                      setInsightsPrefs({ enabled: checked })
                    }}
                  />
                </div>
                <div className='flex items-center justify-between gap-2'>
                  <label
                    htmlFor='insights-auto-open-switch'
                    className={`text-sm select-none ${insightsPrefs.enabled ? 'cursor-pointer' : 'opacity-50'}`}
                  >
                    Auto-open drawer
                  </label>
                  <LBSwitch
                    id='insights-auto-open-switch'
                    checked={insightsPrefs.autoOpen}
                    onCheckedChange={checked => {
                      trackAction('insights_autoopen_toggled', {
                        autoOpen: checked
                      })
                      setInsightsPrefs({ autoOpen: checked })
                    }}
                    disabled={!insightsPrefs.enabled}
                  />
                </div>
              </div>
              <LBDropdownMenuSeparator />
              <LBDropdownMenuLabel>Columns</LBDropdownMenuLabel>
              <LBDropdownMenuSeparator />
              <div className='flex flex-col gap-3 px-2 py-3'>
                {COLUMN_TOGGLES.filter(
                  // Health comes from Salesforce/BigQuery
                  ({ key }) => HAS_US_DATA_SOURCES || key !== 'showHealth'
                ).map(({ key, label, id }) => (
                  <div
                    key={key}
                    className='flex items-center justify-between gap-2'
                  >
                    <label
                      htmlFor={id}
                      className='text-sm cursor-pointer select-none'
                    >
                      {label}
                    </label>
                    <LBSwitch
                      id={id}
                      checked={insightsPrefs[key]}
                      onCheckedChange={checked =>
                        setInsightsPrefs({ [key]: checked })
                      }
                    />
                  </div>
                ))}
              </div>
              <LBDropdownMenuSeparator />
              <LBDropdownMenuLabel>Display</LBDropdownMenuLabel>
              <LBDropdownMenuSeparator />
              <div className='flex flex-col gap-3 px-2 py-3'>
                <div className='flex items-center justify-between gap-2'>
                  <label
                    htmlFor='use-24-hour-switch'
                    className='text-sm cursor-pointer select-none'
                  >
                    Display 24h format
                  </label>
                  <LBSwitch
                    id='use-24-hour-switch'
                    checked={use24Hour}
                    onCheckedChange={setUse24Hour}
                  />
                </div>
                <div className='flex items-center justify-between gap-2'>
                  <label
                    htmlFor='hide-org-logos-switch'
                    className='text-sm cursor-pointer select-none'
                  >
                    Hide org logos
                  </label>
                  <LBSwitch
                    id='hide-org-logos-switch'
                    checked={insightsPrefs.hideOrgLogos}
                    onCheckedChange={checked =>
                      setInsightsPrefs({ hideOrgLogos: checked })
                    }
                  />
                </div>
              </div>
            </LBDropdownMenuContent>
          </LBDropdownMenu>

          <div className='hidden md:flex items-center gap-4'>
            {/* Theme Toggle */}
            <LBDropdownMenu>
              <LBDropdownMenuTrigger asChild>
                <LBButton
                  variant='ghost'
                  size='icon'
                  className='h-9 w-9'
                  aria-label='Toggle theme'
                >
                  {theme === 'light' && <Sun className='h-4 w-4' />}
                  {theme === 'dark' && <Moon className='h-4 w-4' />}
                  {theme === 'system' && <Monitor className='h-4 w-4' />}
                </LBButton>
              </LBDropdownMenuTrigger>
              <LBDropdownMenuContent align='end'>
                <LBDropdownMenuItem
                  onClick={() => setTheme('light')}
                  className='cursor-pointer'
                >
                  <Sun className='h-4 w-4 mr-2' />
                  Light
                </LBDropdownMenuItem>
                <LBDropdownMenuItem
                  onClick={() => setTheme('dark')}
                  className='cursor-pointer'
                >
                  <Moon className='h-4 w-4 mr-2' />
                  Dark
                </LBDropdownMenuItem>
                <LBDropdownMenuItem
                  onClick={() => setTheme('system')}
                  className='cursor-pointer'
                >
                  <Monitor className='h-4 w-4 mr-2' />
                  System
                </LBDropdownMenuItem>
              </LBDropdownMenuContent>
            </LBDropdownMenu>

            {/* User Menu */}
            {userInfo && (
              <LBDropdownMenu>
                <LBDropdownMenuTrigger asChild>
                  <LBButton
                    variant='ghost'
                    className='flex items-center gap-2 p-1'
                  >
                    <LBAvatar className='h-8 w-8'>
                      {userInfo.avatar_url && (
                        <LBAvatarImage
                          src={userInfo.avatar_url}
                          alt={userInfo.name}
                        />
                      )}
                      <LBAvatarFallback className='bg-primary text-primary-foreground text-xs'>
                        {getInitials(userInfo.name)}
                      </LBAvatarFallback>
                    </LBAvatar>
                  </LBButton>
                </LBDropdownMenuTrigger>
                <LBDropdownMenuContent align='end' className='w-64'>
                  <LBDropdownMenuLabel className='font-normal'>
                    <div className='flex flex-col space-y-1'>
                      <p className='text-sm font-medium'>{userInfo.name}</p>
                      <p className='text-xs text-muted-foreground'>
                        {userInfo.email}
                      </p>
                    </div>
                  </LBDropdownMenuLabel>
                  <LBDropdownMenuSeparator />
                  <LBDropdownMenuLabel className='font-normal'>
                    <div className='flex flex-col space-y-2'>
                      <div className='flex items-center justify-between'>
                        <p className='text-xs font-medium text-muted-foreground'>
                          Scopes
                        </p>
                        <LBPopover>
                          <LBPopoverTrigger asChild>
                            <LBButton
                              variant='ghost'
                              size='icon'
                              className='h-5 w-5 p-0'
                            >
                              <Info className='h-3 w-3 text-muted-foreground' />
                            </LBButton>
                          </LBPopoverTrigger>
                          <LBPopoverContent
                            className='w-[600px]'
                            align='end'
                            side='left'
                          >
                            <div className='space-y-2'>
                              <h4 className='font-semibold text-sm'>
                                Available Permissions
                              </h4>
                              <p className='text-xs text-muted-foreground'>
                                Scopes control what you can edit in the
                                Backoffice
                              </p>
                              <LBTable>
                                <LBTableHeader>
                                  <LBTableRow>
                                    <LBTableHead className='text-xs'>
                                      Scope
                                    </LBTableHead>
                                    <LBTableHead className='text-xs'>
                                      What You Can Do
                                    </LBTableHead>
                                    <LBTableHead className='text-xs'>
                                      Where
                                    </LBTableHead>
                                  </LBTableRow>
                                </LBTableHeader>
                                <LBTableBody>
                                  {getAllScopeDefinitions().map(definition => {
                                    // Real scopes: this whole section describes
                                    // what you hold, "View as" what you render as
                                    const hasScope =
                                      userInfo?.realScopes?.includes(
                                        definition.name
                                      )
                                    return (
                                      <LBTableRow key={definition.name}>
                                        <LBTableCell className='py-2'>
                                          <LBBadge
                                            variant={
                                              hasScope ? 'secondary' : 'outline'
                                            }
                                            className={`text-xs font-mono ${!hasScope ? 'opacity-50' : ''}`}
                                          >
                                            {definition.name}
                                          </LBBadge>
                                        </LBTableCell>
                                        <LBTableCell
                                          className={`text-xs py-2 ${!hasScope ? 'text-muted-foreground opacity-50' : ''}`}
                                        >
                                          {definition.description}
                                        </LBTableCell>
                                        <LBTableCell
                                          className={`text-xs py-2 text-muted-foreground ${!hasScope ? 'opacity-50' : ''}`}
                                        >
                                          {definition.location}
                                        </LBTableCell>
                                      </LBTableRow>
                                    )
                                  })}
                                </LBTableBody>
                              </LBTable>
                            </div>
                          </LBPopoverContent>
                        </LBPopover>
                      </div>
                      <BackofficeScopeBadges scopes={userInfo?.realScopes} />
                    </div>
                  </LBDropdownMenuLabel>
                  <LBDropdownMenuSeparator />
                  <ScopePreviewMenu />
                  {hasRoute('backoffice-users') &&
                    userInfo?.scopes?.includes('admin') && (
                      <>
                        <LBDropdownMenuSeparator />
                        <LBDropdownMenuItem
                          onClick={() => navigate('/backoffice-users')}
                          className='cursor-pointer'
                        >
                          Backoffice Users
                        </LBDropdownMenuItem>
                      </>
                    )}
                  <LBDropdownMenuSeparator />
                  {hasRoute('connect-ai') && (
                    <LBDropdownMenuItem
                      onClick={() => navigate('/connect-ai')}
                      className='cursor-pointer'
                    >
                      Connect AI Assistant
                    </LBDropdownMenuItem>
                  )}
                  <LBDropdownMenuItem
                    onClick={handleLogout}
                    className='text-destructive cursor-pointer'
                  >
                    Logout
                  </LBDropdownMenuItem>
                </LBDropdownMenuContent>
              </LBDropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Mobile navigation sheet */}
      <LBSheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <LBSheetContent side='left' className='w-72 p-0 flex flex-col'>
          <LBSheetHeader className='p-4 border-b border-border'>
            <div className='flex items-center justify-between'>
              <LBSheetTitle className='text-lg font-semibold'>
                Navigation
              </LBSheetTitle>
            </div>
          </LBSheetHeader>

          {/* the sheet is taller than a phone viewport, so the body scrolls */}
          <LBScrollArea className='flex-1'>
            {/* Navigation links */}
            <nav className='flex flex-col p-3 gap-1'>
              {visibleRoutes.map(route => (
                <button
                  type='button'
                  key={route.key}
                  onClick={() => {
                    navigate(route.route)
                    setMobileMenuOpen(false)
                  }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm text-left transition-colors ${
                    activeTab === route.key
                      ? 'bg-accent font-medium text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  {route.icon}
                  {route.name}
                </button>
              ))}
            </nav>

            {/* EnvironmentSelector renders nothing for an unlisted API endpoint
              (localhost), so the heading and its divider must go with it */}
            {CURRENT_ENVIRONMENT && (
              <>
                <LBSeparator />
                <div className='p-4'>
                  <p className='text-xs font-medium text-muted-foreground mb-2'>
                    Environment
                  </p>
                  <EnvironmentSelector />
                </div>
              </>
            )}

            <LBSeparator />

            {/* Theme selector */}
            <div className='p-4'>
              <p className='text-xs font-medium text-muted-foreground mb-2'>
                Theme
              </p>
              <div className='flex gap-1'>
                {[
                  { value: 'light' as const, icon: Sun, label: 'Light' },
                  { value: 'dark' as const, icon: Moon, label: 'Dark' },
                  { value: 'system' as const, icon: Monitor, label: 'System' }
                ].map(({ value, icon: Icon, label }) => (
                  <LBButton
                    key={value}
                    variant={theme === value ? 'secondary' : 'ghost'}
                    size='sm'
                    className='flex-1'
                    onClick={() => setTheme(value)}
                  >
                    <Icon className='h-3.5 w-3.5 mr-1.5' />
                    {label}
                  </LBButton>
                ))}
              </div>
            </div>

            <LBSeparator />

            {/* User info */}
            {userInfo && (
              <div className='p-4'>
                <div className='flex items-center gap-3 mb-3'>
                  <LBAvatar className='h-8 w-8'>
                    {userInfo.avatar_url && (
                      <LBAvatarImage
                        src={userInfo.avatar_url}
                        alt={userInfo.name}
                      />
                    )}
                    <LBAvatarFallback className='bg-primary text-primary-foreground text-xs'>
                      {getInitials(userInfo.name)}
                    </LBAvatarFallback>
                  </LBAvatar>
                  <div className='flex flex-col'>
                    <p className='text-sm font-medium'>{userInfo.name}</p>
                    <p className='text-xs text-muted-foreground'>
                      {userInfo.email}
                    </p>
                  </div>
                </div>

                {/* Scopes */}
                <div className='mb-3'>
                  <p className='text-xs font-medium text-muted-foreground mb-1'>
                    Scopes
                  </p>
                  <BackofficeScopeBadges scopes={userInfo?.realScopes} />
                </div>

                <LBSeparator className='my-3' />

                {hasRoute('backoffice-users') &&
                  userInfo?.scopes?.includes('admin') && (
                    <button
                      type='button'
                      onClick={() => {
                        navigate('/backoffice-users')
                        setMobileMenuOpen(false)
                      }}
                      className='w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-accent rounded-md'
                    >
                      Backoffice Users
                    </button>
                  )}
                {hasRoute('connect-ai') && (
                  <button
                    type='button'
                    onClick={() => {
                      navigate('/connect-ai')
                      setMobileMenuOpen(false)
                    }}
                    className='w-full text-left px-3 py-2 text-sm text-muted-foreground hover:bg-accent rounded-md'
                  >
                    Connect AI Assistant
                  </button>
                )}
                <button
                  type='button'
                  onClick={() => {
                    handleLogout()
                    setMobileMenuOpen(false)
                  }}
                  className='w-full text-left px-3 py-2 text-sm text-destructive hover:bg-accent rounded-md'
                >
                  Logout
                </button>
              </div>
            )}
          </LBScrollArea>
        </LBSheetContent>
      </LBSheet>
    </header>
  )
}

export default TabNavigation
